import React, { useEffect, useRef, useState } from "react";
import { Modal } from "components/Modal";
import { Button } from "components/Button";
import styles from "css_modules/LoadAIModal.module.css";
import { request } from "helpers/tools";
import * as pdfjsLib from "pdfjs-dist";
import "pdfjs-dist/build/pdf.worker.entry";

export const LoadAIModal = ({ close, user, doc, loadFromAI, docTypes = [] }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recommendations, setRecommendations] = useState([]);

  useEffect(() => {
    if (!doc?.id) return;

    const handleSendAIRequest = async () => {
      setLoading(true);
      setError(null);

      try {
        const body = {
        id: 7931,//doc?.id,
        accounting_company_id: 1,//user?.company_title,
        supplier_title: "Giri s.r.o.",//doc?.document_info?.supplier_title,
        batch_size: 3,
      };

        const res = await request("/ai_recommendations/", "POST", body);

        if (res?.ok) {
          const data = res.body;
          const recList = data?.recommendations || (Array.isArray(data) ? data : data?.results || []);

          // Fetch archive and detail endpoints for each recommended UUID
          const docsWithDetails = await Promise.all(
            recList.map(async (rec) => {
              const uuid = typeof rec === "string" ? rec : rec?.uuid;
              if (!uuid) return rec;

              const [archiveRes, detailRes] = await Promise.all([
                request(`/archive/${uuid}/`, "GET"),
                request(`/detail/${uuid}/`, "GET"),
              ]);

              return {
                ...rec,
                uuid,
                archiveData: archiveRes?.ok ? archiveRes.body : null,
                detailData: detailRes?.ok ? detailRes.body : null,
              };
            })
          );

          setRecommendations(docsWithDetails);
        } else {
          setError(`Request failed with status: ${res?.status}`);
        }
      } catch (err) {
        console.error("AI recommendation request error:", err);
        setError("Při načítání AI doporučení došlo k chybě.");
      } finally {
        setLoading(false);
      }
    };

    handleSendAIRequest();
  }, [doc?.id, doc?.document_info?.supplier_title, user?.company_title]);

  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const canvasRef = useRef(null);

  const hoveredRec = hoveredIndex !== null ? recommendations[hoveredIndex] : null;
  const previewDoc = hoveredRec?.archiveData || hoveredRec?.detailData;
  const pdfUrl = previewDoc?.file_url;
  const isPdf = pdfUrl && pdfUrl.toLowerCase().includes(".pdf");

  useEffect(() => {
    if (!isPdf || !pdfUrl || !canvasRef.current) return;

    let isCancelled = false;
    let renderTask = null;

    const loadPdf = async () => {
      try {
        const loadingTask = pdfjsLib.getDocument(pdfUrl);
        const pdf = await loadingTask.promise;
        if (isCancelled) return;

        const page = await pdf.getPage(1);
        if (isCancelled) return;

        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext("2d");
        canvas.height = viewport.height;
        canvas.width = viewport.width;

        renderTask = page.render({
          canvasContext: context,
          viewport,
        });

        await renderTask.promise;
      } catch (err) {
        if (!isCancelled) {
          console.error("PDF load error:", err);
        }
      }
    };

    loadPdf();

    return () => {
      isCancelled = true;
      if (renderTask) {
        renderTask.cancel();
      }
    };
  }, [pdfUrl, isPdf, hoveredIndex]);

  if (loading) {
    return (
      <div className={styles.ontop}>
        <Modal name={"Nabízené doklady"} close={close}>
          <div className={styles.docColumn} style={{ padding: "2rem" }}>
            Načítání AI doporučení...
          </div>
        </Modal>
      </div>
    );
  }

  const selectTemplateOverwrite = () => {
    if (selectedIndex !== null && recommendations[selectedIndex]) {
      const selected = recommendations[selectedIndex];
      const targetDetail = selected.detailData || selected.archiveData || selected;
      loadFromAI(targetDetail, true);
      close();
    }
  };

  const selectTemplateAdd = () => {
    if (selectedIndex !== null && recommendations[selectedIndex]) {
      const selected = recommendations[selectedIndex];
      const targetDetail = selected.detailData || selected.archiveData || selected;
      loadFromAI(targetDetail, false);
      close();
    }
  };

  return (
    <div className={styles.ontop}>
      <Modal name={"Nabízené doklady"} close={close}>
        <div className={styles.docColumn}>
          {error && <p className={styles.error} style={{ padding: "1rem", color: "red" }}>{error}</p>}

          <table className={styles.table}>
            <thead>
              <tr>
                <th className={styles.th}>#</th>
                <th className={styles.th}>Popis dokladu</th>
                <th className={styles.th}>Partner</th>
              </tr>
            </thead>

            <tbody>
              {recommendations.map((item, index) => {
                const isSelected = selectedIndex === index;
                const docInfo = item.archiveData?.document_info || item.detailData?.document_info || item.document_info;

                return (
                  <React.Fragment key={item.uuid || index}>
                    <tr
                      className={`${styles.tr} ${isSelected ? styles.selectedRow : ""}`}
                      onClick={() => setSelectedIndex((prev) => (prev === index ? null : index))}
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => setHoveredIndex(null)}
                      onMouseMove={(e) => setMousePos({ x: e.clientX, y: e.clientY })}
                    >
                      <td className={styles.td}>{index + 1}</td>
                      <td className={styles.td}>{docInfo?.document_identifier || "—"}</td>
                      <td className={styles.td}>{docInfo?.supplier_title || "—"}</td>
                    </tr>

                    {isSelected && (
                      <tr className={styles.expandedRow}>
                        <td colSpan={3}>
                          <div className={styles.expandWrapper}>
                            <div className={styles.inlineActions}>
                              <Button primary onClick={selectTemplateOverwrite}>
                                Načíst data
                              </Button>
                              <Button primary onClick={selectTemplateAdd}>
                                Doplnit data
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {isPdf && hoveredIndex !== null && (
          <div
            className={styles.hoverPreview}
            style={{
              top: mousePos.y + 15,
              left: mousePos.x + 15,
            }}
          >
            <canvas ref={canvasRef} className={styles.pdfCanvas} />

            <div className={styles.previewMeta}>
              <div>
                <strong>Partner:</strong> {previewDoc?.document_info?.supplier_title || "—"}
              </div>
              <div>
                <strong>Popis dokladu:</strong> {previewDoc?.document_info?.document_identifier || "—"}
              </div>
              <div>
                <strong>Datum:</strong> {previewDoc?.document_info?.date || "—"}
              </div>
              <div>
                <strong>Typ dokladu:</strong>{" "}
                {docTypes.find((item) => item.slug === previewDoc?.document_info?.document_type)?.name || "-"}
              </div>
              <div>
                <strong>Středisko:</strong> {previewDoc?.document_info?.centre_title || "—"}
              </div>
              <div>
                <strong>Číselná řada:</strong> {previewDoc?.document_info?.num_series_title || "—"}
              </div>
              <div>
                <strong>Předkontace:</strong> {previewDoc?.document_info?.account_code_title || "—"}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};